import { capturePrivateSession, isPrivateSessionCurrent, getPrivateSessionState } from "@/lib/api/private-session";
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { switchPrivateStore } from "@/shared/privateSessionChange";
import { useErrorMessage } from "@/shared/errors";
import { useSession } from "./useSession";
import { useStoreBindingAttempt } from "@/shared/storeBindingAttempt";
import type { SellerSession } from "@/lib/api/types";

/**
 * IS THIS SESSION ON A RESTAURANT YET, AND THE ONE-SHOT REPAIR IF IT IS NOT.
 *
 * Seller login binds `stores[0]` (`seller-auth.service.ts`), so an owner who
 * holds a `DEFAULT` store and a `RESTAURANT` one lands on the wrong one — P5.3b.
 *
 * Extracted from `ProtectedLayout` because a SECOND screen has to know the same
 * thing, and for a reason that is not cosmetic. **Where a session lands is
 * decided from its permissions**, and on the wrong store those permissions are
 * the wrong store's: an owner holding `RESTAURANT_POS_USE` on their restaurant
 * has none of it on their clothing shop, so `landingSurfaceFor` read "no till"
 * and sent them to the back office. The switch then fixed the binding behind
 * them and nothing re-decided, so the person who IS the cashier started every
 * shift one navigation away from the till. The answer is not to re-land after
 * the fact — it is to not answer a question whose inputs are known to be wrong,
 * which is what `isPending` below is for.
 *
 * One automatic attempt only. A failed auto-switch must degrade to the visible
 * picker and its error, never to a silent retry loop.
 */
export type StoreBinding = {
  session: SellerSession | undefined;
  isLoading: boolean;
  error: unknown;
  /** The bound store is not a restaurant. */
  wrongStore: boolean;
  /** The RESTAURANT stores this account could switch to, minus the bound one. */
  restaurantStores: NonNullable<SellerSession["availableStores"]>;
  /**
   * The binding is wrong and being repaired without asking, because there is
   * exactly one answer. **Nothing that reads permissions may decide while this
   * is true** — that is the whole reason this flag is public.
   */
  isPending: boolean;
  switchError: string | null;
  switchingTo: string | null;
  isSwitching: boolean;
  switchTo: (storeSlug: string) => Promise<void>;
};

export function useStoreBinding(): StoreBinding {
  const lifetime = useRef(capturePrivateSession()).current;
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const { data, isLoading, error } = useSession();
  const bindingAttempt = useStoreBindingAttempt();
  const attempt = bindingAttempt.attempt;
  const interrupted = attempt?.generation !== getPrivateSessionState().generation;
  const switchError = attempt?.error ?? (attempt && interrupted ? errorMessage(new DOMException("Session changed", "AbortError")) : null);
  const switchingTo = attempt?.pending && !interrupted ? attempt.target : null;

  const wrongStore = data != null && data.seller.storeKind !== "RESTAURANT";
  const restaurantStores = (data?.availableStores ?? []).filter(
    (store) =>
      store.storeSlug !== data?.seller.storeSlug &&
      store.storeKind === "RESTAURANT",
  );

  const switchTo = async (storeSlug: string, manual = true) => {
    // A superseded effect owns no switch, even before the one-shot claim.
    if (!isPrivateSessionCurrent(lifetime)) return;
    const token = bindingAttempt.claim(storeSlug, manual);
    if (!token) return;
    try {
      const pending = switchPrivateStore(queryClient, storeSlug);
      bindingAttempt.own(token);
      await pending;
      bindingAttempt.finish(token, null);
    } catch (failure) {
      bindingAttempt.finish(token, errorMessage(failure));
    }
  };

  /*
   * P5.3b as specified: with EXACTLY ONE restaurant on the account there is no
   * choice to offer — the picker was a tap the owner paid on every login just to
   * answer a question with one answer. Switch silently; the picker remains for
   * the genuinely ambiguous multi-restaurant account and for auto-failure.
   */
  const canAutoSwitch = wrongStore && restaurantStores.length === 1;
  useEffect(() => {
    if (!canAutoSwitch || attempt?.target === restaurantStores[0].storeSlug) return;
    void switchTo(restaurantStores[0].storeSlug, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on arrival
  }, [canAutoSwitch]);

  return {
    session: data,
    isLoading,
    error,
    wrongStore,
    restaurantStores,
    // `!switchError`: once the automatic attempt has failed there is nothing
    // pending any more — the picker owns the screen and the caller may render.
    isPending: canAutoSwitch && (!attempt || (attempt.pending && !interrupted)),
    switchError,
    switchingTo,
    isSwitching: switchingTo !== null,
    switchTo,
  };
}
